/**
 * The roster of managers.
 *
 * Two quite different reasons to open this, and the screen has to serve both without confusing
 * them:
 *
 *   Somebody joins. A seat changes hands and the new man needs to exist before he can sign in,
 *   hold a team, appear in the FAAB ledger or be picked in the lottery. He also, almost always,
 *   inherits a roster from whoever he replaced — so replacing is offered as a single action that
 *   retires the old manager and seats the new one, rather than two edits a person has to
 *   remember to do in the right order.
 *
 *   Somebody is remembered. Half of 2003-2008 belongs to managers who left before Yahoo started
 *   recording names, and the pairing screen cannot pair a team to a man the app has never heard
 *   of. Adding a former manager is therefore a first-class action, not an afterthought.
 *
 * The id is the part worth being careful about, and the screen says so: names are cosmetic and
 * can be changed at will, but the id is what every pairing, trade and declaration is keyed on.
 * It is derived from the name, shown before saving, and locked once the manager exists.
 *
 * Nothing is ever deleted. A manager who leaves becomes `former` and keeps his seasons.
 */

import { useEffect, useMemo, useState } from 'react';
import { careerTable } from '../domain/records.js';
import { usePairingVersion } from './pairings.js';
import {
  ROSTER_SOURCE, allManagers, isAdded, managerRole, suggestId, suggestMark, validateManager,
  type Manager,
} from '../domain/identity.js';
import { GmBadge } from './GmBadge.js';
import { GateTag } from './Gate.js';
import { useRosterVersion } from './pairings.js';
import { atLeast, type LeagueManager, type LeagueStore, type Role } from '../store/types.js';
import { asFirestore, asMemoryStore } from '../store/provider.js';
import { note } from './auditor.js';

const SEASON = 2026;

type Draft = {
  id: string;
  name: string;
  status: 'current' | 'former';
  role: Role;
  team: string;
  mark: string;
  seasons: string;
  note: string;
  /** Set when this manager is taking a seat off somebody who is leaving. */
  replaces: string;
};

const BLANK: Draft = {
  id: '', name: '', status: 'current', role: 'manager', team: '', mark: '', seasons: '',
  note: '', replaces: '',
};

export function ManagerRoster(
  { actor, role, store }: { actor: string; role: Role; store: LeagueStore },
) {
  const version = useRosterVersion();
  /**
   * ADMIN, not commissioner.
   *
   * Everything else a commissioner does is reversible and visible — a trade recorded wrongly is
   * an entry somebody can see and remove. This screen decides who exists and what they can do,
   * and granting access is the one action in the app that cannot be undone by the person it was
   * done to. So it sits one rung higher, and everybody else reads it.
   */
  const canEdit = atLeast(role, 'admin');
  // Careers move when a 2004 team gets paired up, so this follows the pairings too.
  const pairings = usePairingVersion();
  const careers = useMemo(() => {
    const by = new Map<string, { w: number; l: number; seasons: number; titles: number }>();
    for (const r of careerTable().rows) {
      by.set(r.gm, { w: r.w, l: r.l, seasons: r.seasons, titles: r.championships });
    }
    return by;
  }, [`${version}.${pairings}`]);
  const [saved, setSaved] = useState<LeagueManager[]>([]);
  const [form, setForm] = useState<Draft | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const load = () => void store.managers().then((r) => { if (live) setSaved(r); });
    load();
    const off = store.subscribe(SEASON, (w) => { if (w === 'managers') load(); });
    return () => { live = false; off(); };
  }, [store]);

  const roster = useMemo(() => {
    const all = allManagers();
    return {
      current: all.filter((m) => m.status === 'current').sort((a, b) => a.id.localeCompare(b.id)),
      former: all.filter((m) => m.status === 'former')
        .sort((a, b) => (b.seasons?.at(-1) ?? 0) - (a.seasons?.at(-1) ?? 0) || a.id.localeCompare(b.id)),
    };
  }, [version]);

  const say = (msg: string) => {
    setFlash(msg);
    setTimeout(() => setFlash(null), 5000);
  };

  /** Typing a name proposes an id and a badge — both editable, both shown before saving. */
  function onName(name: string) {
    if (!form) return;
    const id = editing ? form.id : suggestId(name);
    setForm({ ...form, name, id, mark: editing ? form.mark : (suggestMark(id, name) ?? '') });
  }

  const problems = form
    ? validateManager(
      {
        id: form.id,
        name: form.name,
        status: form.status,
        ...(form.team.trim() ? { team: form.team.trim() } : {}),
      },
      editing ?? undefined,
    )
    : [];

  async function save() {
    if (!form || problems.length > 0) return;
    const nextOrder = Math.max(-1, ...allManagers().map((m) => m.order ?? -1)) + 1;
    const existing = allManagers().find((m) => m.id === form.id.toUpperCase());
    const seasons = form.seasons
      .split(/[^0-9]+/).map(Number).filter((n) => n >= 2003 && n <= 2035);

    const record: LeagueManager = {
      id: form.id.toUpperCase(),
      name: form.name.trim(),
      status: form.status,
      role: form.role,
      ...(form.team.trim() ? { team: form.team.trim() } : {}),
      ...(form.mark.trim() ? { mark: form.mark.trim().toUpperCase() } : {}),
      order: existing?.order ?? nextOrder,
      ...(seasons.length ? { seasons } : {}),
      ...(form.note.trim() ? { note: form.note.trim() } : {}),
      addedBy: actor,
      addedAt: new Date().toISOString(),
    };
    await store.saveManager(record);

    // Replacing somebody is one action: the outgoing manager is retired and keeps his seasons,
    // and the incoming one takes the seat. Doing it as two edits is how a league ends up with
    // eleven "current" managers and nobody noticing until draft night.
    if (form.replaces) {
      const out = allManagers().find((m) => m.id === form.replaces);
      if (out) {
        await store.saveManager({
          id: out.id,
          name: out.name,
          status: 'former',
          role: out.role ?? 'manager',
          ...(out.mark ? { mark: out.mark } : {}),
          ...(out.order !== undefined ? { order: out.order } : {}),
          ...(out.seasons?.length ? { seasons: out.seasons } : {}),
          note: `${out.note ? `${out.note} ` : ''}Seat passed to ${record.name} in ${SEASON}.`,
          addedBy: actor,
          addedAt: new Date().toISOString(),
        });
      }
      say(`${record.name} takes ${out?.name ?? form.replaces}'s seat — he is now a former manager.`);
    } else {
      say(`${editing ? 'Updated' : 'Added'} ${record.name} (${record.id}).`);
    }

    setForm(null);
    setEditing(null);
  }

  function startEdit(m: Manager) {
    setEditing(m.id);
    setForm({
      id: m.id,
      name: m.name,
      status: m.status,
      role: (m.role ?? 'manager') as Role,
      team: m.team ?? '',
      mark: m.mark ?? '',
      seasons: (m.seasons ?? []).join(', '),
      note: m.note ?? '',
      replaces: '',
    });
  }

  /**
   * What deleting him would orphan.
   *
   * The roster's rule is that nobody is ever deleted, because his seasons are in the record — but
   * that rule exists to protect history, not typos. A manager added by mistake, referenced by
   * nothing and not part of the build, should simply go.
   */
  function blockers(m: Manager): Array<{ what: string; n: number }> {
    // Both stores answer this; only the offline preview has neither, and there nothing is added.
    const holder = asMemoryStore(store) ?? asFirestore(store);
    const out = holder ? holder.usage(m.id) : [];
    if (m.status === 'current' && m.team) out.unshift({ what: 'team seat', n: 1 });
    return out;
  }

  /** Only a manager the app itself added, with nothing pointing at him, can be removed. */
  const removable = (m: Manager) => isAdded(m.id) && blockers(m).length === 0;

  async function remove(m: Manager) {
    note(store, actor, {
      kind: 'delete', what: `Removed ${m.id} from the roster`, target: `managers/${m.id}`,
    });
    await store.deleteManager(m.id);
    say(`Deleted ${m.name}. He was added here and nothing referred to him.`);
  }

  async function retire(m: Manager) {
    await store.saveManager({
      id: m.id,
      name: m.name,
      status: 'former',
      role: m.role ?? 'manager',
      ...(m.mark ? { mark: m.mark } : {}),
      ...(m.order !== undefined ? { order: m.order } : {}),
      ...(m.seasons?.length ? { seasons: m.seasons } : {}),
      ...(m.note ? { note: m.note } : {}),
      addedBy: actor,
      addedAt: new Date().toISOString(),
    });
    say(`${m.name} is now a former manager. His seasons stay in the record.`);
  }

  return (
    <section className={`card roster ${canEdit ? 'gated admin' : ''}`}>
      {canEdit && <GateTag level="admin" what="Admin only — who exists, and what they can do" />}
      <div className="striptop">
        <h3 className="striphead">Managers</h3>
        <span className="mut">
          {roster.current.length} playing now · {roster.former.length} who have moved on
        </span>
        {canEdit && !form && (
          <button type="button" className="btn" onClick={() => setForm({ ...BLANK })}>
            Add a manager
          </button>
        )}
      </div>

      <p className="note">
        {canEdit
          ? 'Everyone the league has ever had, and the only place a manager can be created or '
            + 'given access. A man has to exist here before he can be paired to an old team, be '
            + 'picked in the lottery, hold a keeper slate or sign in — including somebody nobody '
            + 'has thought about since 2005.'
          : 'Everyone the league has ever had, with the seasons they played and the team names '
            + 'they played under. Names before 2009 come out of the league’s own memory; Yahoo '
            + 'kept none of it.'}
      </p>

      {flash && <p className="chip good block">{flash}</p>}

      {form && (
        <div className="mgrform">
          <h4>{editing ? `Editing ${editing}` : 'New manager'}</h4>
          <div className="formrow">
            <label>
              Name
              <input value={form.name} placeholder="John Wardach" autoFocus
                onChange={(e) => onName(e.target.value)} />
            </label>
            <label className="idfield">
              Id
              <input value={form.id} disabled={Boolean(editing)}
                onChange={(e) => setForm({ ...form, id: e.target.value.toUpperCase() })} />
              <span className="mut hint">
                {editing
                  ? 'Permanent — every pairing and trade is keyed on it.'
                  : 'Derived from the name. Change it now if you want to; it is fixed after saving.'}
              </span>
            </label>
            <label className="markfield">
              Badge
              <input value={form.mark} maxLength={3} placeholder="auto"
                onChange={(e) => setForm({ ...form, mark: e.target.value.toUpperCase() })} />
            </label>
            {form.id && form.name && (
              <span className="mgrpreview">
                <GmBadge gm={form.id} withName />
              </span>
            )}
          </div>

          <div className="formrow">
            <label>
              Status
              <select value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as 'current' | 'former' })}>
                <option value="current">Current — holds a team now</option>
                <option value="former">Former — played in the past</option>
              </select>
            </label>
            <label>
              Access
              <select value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
                <option value="manager">Manager</option>
                <option value="commissioner">Commissioner</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            {form.status === 'current' && (
              <label className="wide">
                Team
                <input value={form.team} placeholder="His 2026 team name"
                  onChange={(e) => setForm({ ...form, team: e.target.value })} />
              </label>
            )}
            {form.status === 'former' && (
              <label className="wide">
                Seasons played
                <input value={form.seasons} placeholder="2003, 2004, 2005"
                  onChange={(e) => setForm({ ...form, seasons: e.target.value })} />
              </label>
            )}
          </div>

          {form.status === 'current' && !editing && (
            <div className="formrow">
              <label className="wide">
                Taking over from
                <select
                  value={form.replaces}
                  onChange={(e) => {
                    // Taking a seat means taking the team attached to it — the keeper rows, the
                    // farm log and the draft board are all keyed on that name. Renaming it is a
                    // separate act (and, under Winner Names Loser, not always his to make).
                    const out = allManagers().find((m) => m.id === e.target.value);
                    setForm({
                      ...form,
                      replaces: e.target.value,
                      team: out?.team ?? form.team,
                    });
                  }}
                >
                  <option value="">Nobody — this is an extra seat</option>
                  {roster.current.map((m) => (
                    <option key={m.id} value={m.id}>{m.name} ({m.id})</option>
                  ))}
                </select>
                <span className="mut hint">
                  Retires him in the same action, keeps his seasons, and moves the team across so
                  the keeper board follows the seat. Career records stay per manager — an
                  inherited roster is not an inherited record.
                </span>
              </label>
            </div>
          )}

          <div className="formrow">
            <label className="wide">
              Note
              <input value={form.note} placeholder="Anything worth remembering about him"
                onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </label>
          </div>

          {problems.length > 0 && (
            <ul className="tight warnlist">{problems.map((p) => <li key={p}>{p}</li>)}</ul>
          )}

          <div className="actions">
            <button type="button" className="btn"
              onClick={() => { setForm(null); setEditing(null); }}>Cancel</button>
            <button type="button" className="btn primary" disabled={problems.length > 0}
              onClick={() => void save()}>
              {editing ? 'Save changes' : 'Add manager'}
            </button>
          </div>
        </div>
      )}

      <Group title="Playing now" managers={roster.current} canEdit={canEdit} careers={careers}
        onEdit={startEdit} onRetire={retire} onRemove={remove} removable={removable}
        blockers={blockers} savedIds={saved.map((s) => s.id)} />
      <Group title="Moved on" managers={roster.former} canEdit={canEdit} careers={careers}
        onEdit={startEdit} onRemove={remove} removable={removable}
        blockers={blockers} savedIds={saved.map((s) => s.id)} />

      <p className="note src">
        {ROSTER_SOURCE}
        {saved.length > 0 && (
          <>
            {' '}
            <b>
              {saved.length} change{saved.length === 1 ? '' : 's'} made in the app are held for
              this session only until the Firestore backend is wired.
            </b>
          </>
        )}
      </p>
    </section>
  );
}

function Group(
  { title, managers, canEdit, careers, onEdit, onRetire, onRemove, removable, blockers, savedIds }: {
    title: string;
    managers: Manager[];
    canEdit: boolean;
    careers: Map<string, { w: number; l: number; seasons: number; titles: number }>;
    onEdit: (m: Manager) => void;
    onRetire?: (m: Manager) => void;
    onRemove: (m: Manager) => void;
    removable: (m: Manager) => boolean;
    blockers: (m: Manager) => Array<{ what: string; n: number }>;
    savedIds: string[];
  },
) {
  if (managers.length === 0) return null;
  return (
    <>
      <h4 className="mgrgrouphead">{title}</h4>
      <ul className="mgrlist">
        {managers.map((m) => (
          <li key={m.id} className={m.coManagerOf ? 'co' : ''}>
            <GmBadge gm={m.id} />
            <b>{m.name}</b>
            <span className="mut id">{m.id}</span>
            {managerRole(m.id) !== 'manager' && (
              <span className={`chip tiny ${managerRole(m.id) === 'admin' ? 'admin' : 'commish'}`}>
                {managerRole(m.id)}
              </span>
            )}
            {/* A team is a seat, and a former manager does not hold one. */}
            {m.status === 'current' && m.team && <span className="mut team">{m.team}</span>}
            {/*
              A CURRENT MANAGER WITH NO TEAM IS NOT ONE OF THE TEN, and the roll, the draft order
              and every ledger now leave him out — so the row has to say so rather than let him sit
              here looking like the other ten. He cannot be created in this form (the validation
              refuses it); he arrives when an access request is approved. Fix him by giving him a
              team or by marking him former.
            */}
            {m.status === 'current' && !m.team?.trim() && !m.coManagerOf && (
              <span className="chip warning tiny" title="Not counted in votes, the draft order or any ledger">
                no seat
              </span>
            )}
            {m.seasons?.length && (
              <span className="mut yrs">
                {m.seasons[0]}{m.seasons.length > 1 ? `–${m.seasons.at(-1)}` : ''}
              </span>
            )}
            {m.coManagerOf && <span className="mut">co-manager</span>}
            {(isAdded(m.id) || savedIds.includes(m.id)) && (
              <span className="chip warning tiny">added here</span>
            )}
            <History m={m} career={careers.get(m.id)} />
            {canEdit && (
              <span className="rowacts">
                <button type="button" className="linkbtn tiny" onClick={() => onEdit(m)}>edit</button>
                {onRetire && !m.coManagerOf && (
                  <button type="button" className="linkbtn tiny" onClick={() => void onRetire(m)}>
                    retire
                  </button>
                )}
                {removable(m) && (
                  <button type="button" className="linkbtn tiny danger"
                    title="Added here and referred to by nothing — safe to remove"
                    onClick={() => void onRemove(m)}>
                    delete
                  </button>
                )}
                {isAdded(m.id) && !removable(m) && (
                  <span className="mut tiny" title={
                    blockers(m).length > 0
                      ? `Referred to by ${blockers(m).map((x) => `${x.n} ${x.what}${x.n === 1 ? '' : 's'}`).join(', ')} — retire him instead`
                      : ''
                  }>
                    in use
                  </span>
                )}
              </span>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * The line that makes this list worth reading rather than administering.
 *
 * A manager is his seasons, the names he played under and what he did — and for the men nobody
 * has seen since 2005 the team names ARE the memory: "Kumar" means little, "Mexican Ron, Bad Newz
 * Kennels, U Sorry" means a person. Where a career is only partly countable the number is left
 * off rather than quoted short.
 */
function History(
  { m, career }: {
    m: Manager;
    career: { w: number; l: number; seasons: number; titles: number } | undefined;
  },
) {
  const teams = m.teamNames ?? [];
  if (!career && teams.length === 0) return null;
  return (
    <span className="mgrhistory">
      {career && (
        <span className="rec">
          {career.w}-{career.l}
          <span className="mut">
            {' '}in {career.seasons} counted season{career.seasons === 1 ? '' : 's'}
          </span>
          {career.titles > 0 && (
            <span className="titles" title={`${career.titles} championship${career.titles === 1 ? '' : 's'}`}>
              {' '}{'\u2605'.repeat(career.titles)}
            </span>
          )}
        </span>
      )}
      {teams.length > 0 && (
        <span className="mut tms" title={teams.join(' · ')}>
          {teams.slice(0, 4).join(' · ')}{teams.length > 4 ? ` · +${teams.length - 4}` : ''}
        </span>
      )}
    </span>
  );
}
